import { FC, useCallback, useEffect, useState } from 'react';
import { Button, LoadingSpinner, Virtuoso, confirmDialog, globalToastService } from '@repo/atomic-components';
import {
  CaseExpiryDatesNode,
  useLazyGetCasesBasicQuery,
  useLazyGetListCaseExpiryDatesQuery,
  useLazyGetListOrgExpiredCasesQuery,
  usePurgeOrgExpiredCasesMutation,
} from '@/api';
import { PURGE_EXPIRED_CASES_CONFIRM, PURGE_PROTECTED_ORG_NAMES } from '@/constants';
import { formatDateString } from '@/utils';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { WarningConfirmationDialog } from '@/ui/components/WarningConfirmationDialog';
import { DashboardSection } from './DashboardSection';

// Human labels for the backend's ExpiryReasonEnum.
const EXPIRY_REASON_LABELS: Record<string, string> = {
  CASE_CLOSURE: 'Case closure',
  FIRST_UPLOAD: 'First upload',
};

// Fixed-height virtualised rows — expired-case counts can run into the
// hundreds after a retention change, so only the visible rows render.
const ROW_HEIGHT = 40;
const MAX_LIST_HEIGHT = 400;

// One grid template shared by the header and every row so columns line up.
const gridClass = 'grid grid-cols-[minmax(160px,2fr)_repeat(5,1fr)] items-center gap-4 px-4';

interface ExpiredCaseRow extends CaseExpiryDatesNode {
  caseId: string;
  caseReference: string;
}

const formatOptionalDate = (date: string | null) => (date ? formatDateString(date) : '—');

// `daysUntilExpiry` is negative once a case is past its expiry date.
const formatDaysUntilExpiry = (days: number | null) => {
  if (days === null) return '—';
  if (days < 0) return `${Math.abs(days)} days ago`;
  if (days === 0) return 'Today';
  return `In ${days} days`;
};

interface ExpiredCasesSectionProps {
  className?: string;
  /** Name of the currently switched-into org, from `debug/list-orgs`. */
  currentOrgName: string | null;
}

/**
 * Lists the cases that are already past their retention period for the
 * *currently switched-into* org — the set `admin-utils/purge-org-expired-cases`
 * would delete. Loads on mount from `admin-utils/list-org-expired-cases`
 * (ids only), then enriched with case references (`cases/basic`) and retention
 * detail (`cases/list-case-expiry-dates`) so an admin can review before
 * anything is deleted. Refresh re-runs all three; Purge deletes the whole set
 * via `admin-utils/purge-org-expired-cases` behind a confirmation dialog.
 */
export const ExpiredCasesSection: FC<ExpiredCasesSectionProps> = ({ className, currentOrgName }) => {
  // Fletchers and MLS can still review their expired cases, but never purge them.
  // Hidden until the current org name has resolved so it can't flash in.
  const canPurge =
    !!currentOrgName &&
    !PURGE_PROTECTED_ORG_NAMES.some((name) => name.toLowerCase() === currentOrgName.trim().toLowerCase());

  const [triggerListExpiredCases] = useLazyGetListOrgExpiredCasesQuery();
  const [triggerListCaseExpiryDates] = useLazyGetListCaseExpiryDatesQuery();
  const [triggerGetCasesBasic] = useLazyGetCasesBasicQuery();
  const [purgeExpiredCases, { isLoading: isPurging }] = usePurgeOrgExpiredCasesMutation();

  const [rows, setRows] = useState<ExpiredCaseRow[] | null>(null);
  // Starts true — the list loads on mount, so show the spinner from the first
  // paint rather than flashing an empty table.
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchExpiredCases = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const [{ expiredCaseIds }, { caseExpiryDates }, casesBasic] = await Promise.all([
        triggerListExpiredCases().unwrap(),
        triggerListCaseExpiryDates().unwrap(),
        triggerGetCasesBasic({}).unwrap(),
      ]);

      const referenceByCaseId = new Map(casesBasic.map(({ caseId, caseReference }) => [caseId, caseReference]));

      const expiredRows: ExpiredCaseRow[] = expiredCaseIds.map((caseId) => ({
        caseId,
        // Purged / never-listed cases can be missing from cases/basic — fall
        // back to the raw id so the row is still identifiable.
        caseReference: referenceByCaseId.get(caseId) ?? caseId,
        expiryDate: caseExpiryDates[caseId]?.expiryDate ?? null,
        daysUntilExpiry: caseExpiryDates[caseId]?.daysUntilExpiry ?? null,
        dateOfFirstUpload: caseExpiryDates[caseId]?.dateOfFirstUpload ?? null,
        dateOfCaseClosure: caseExpiryDates[caseId]?.dateOfCaseClosure ?? null,
        expiryReason: caseExpiryDates[caseId]?.expiryReason ?? null,
      }));

      // Longest-expired first — that's the order an admin works through them.
      expiredRows.sort((a, b) => (a.expiryDate ?? '').localeCompare(b.expiryDate ?? ''));

      setRows(expiredRows);
    } catch (error) {
      console.error('Failed to fetch expired cases:', error);
      setIsError(true);
      globalToastService.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch expired cases.' });
    } finally {
      setIsLoading(false);
    }
  }, [triggerListExpiredCases, triggerListCaseExpiryDates, triggerGetCasesBasic]);

  // Load once on mount; the Refresh button re-runs the same fetch.
  useEffect(() => {
    fetchExpiredCases();
  }, [fetchExpiredCases]);

  // Permanently deletes every expired case in the current org. Confirmed
  // behind the standard red warning dialog first, then the list is refetched
  // so what's left on screen matches the server.
  const handlePurge = async () => {
    const confirmed = await confirmDialog({ id: PURGE_EXPIRED_CASES_CONFIRM });
    if (!confirmed) return;

    try {
      const { purgedCaseIds } = await purgeExpiredCases().unwrap();
      globalToastService.show({
        severity: 'success',
        summary: 'Expired cases purged',
        detail: `${purgedCaseIds.length} ${purgedCaseIds.length === 1 ? 'case' : 'cases'} permanently deleted.`,
      });
    } catch (error) {
      console.error('Failed to purge expired cases:', error);
      globalToastService.show({ severity: 'error', summary: 'Error', detail: 'Failed to purge expired cases.' });
    } finally {
      // Refetch either way — a partial purge still changes the list.
      fetchExpiredCases();
    }
  };

  const expiredCaseCount = rows?.length ?? 0;

  return (
    <DashboardSection
      title="Expired cases"
      description="Cases past their retention period for your current organisation — the cases a purge would delete."
      className={className}
      action={
        <div className="flex items-center gap-2">
          {/* Both buttons are disabled *and* spinning while anything is in
              flight, so repeated clicks can't stack up requests. */}
          <OutlineButton
            label="Refresh"
            icon="pi pi-refresh"
            disabled={isLoading || isPurging}
            loading={isLoading}
            onClick={fetchExpiredCases}
            style={{ height: 32 }}
          />
          {canPurge && (
            <Button
              label="Purge expired cases"
              icon="pi pi-trash"
              // Nothing to purge until the list has loaded with at least one row.
              disabled={isLoading || isPurging || isError || expiredCaseCount === 0}
              loading={isPurging}
              onClick={handlePurge}
              className="!bg-fletchers-red-500 !border-fletchers-red-500 hover:!bg-fletchers-red-600 !px-4"
              style={{ height: 32 }}
            />
          )}
        </div>
      }
    >
      <WarningConfirmationDialog
        id={PURGE_EXPIRED_CASES_CONFIRM}
        bodyText={`${expiredCaseCount} expired ${expiredCaseCount === 1 ? 'case' : 'cases'} will be permanently deleted. This cannot be undone.`}
        confirmLabel="Purge"
      />

      {rows !== null && !isLoading && !isError && (
        <span className="text-fletchers-gray-200 text-sm">
          {expiredCaseCount} expired {expiredCaseCount === 1 ? 'case' : 'cases'}
        </span>
      )}

      <div className="border-fletchers-gray-110 overflow-hidden rounded-md border">
        <div className={`border-fletchers-gray-110 h-10 border-b ${gridClass}`}>
          <span className="text-fletchers-gray-175 text-[14px]/[20px] font-[600]">Case reference</span>
          <span className="text-fletchers-gray-175 text-[14px]/[20px] font-[600]">Expiry date</span>
          <span className="text-fletchers-gray-175 text-[14px]/[20px] font-[600]">Expired</span>
          <span className="text-fletchers-gray-175 text-[14px]/[20px] font-[600]">Reason</span>
          <span className="text-fletchers-gray-175 text-[14px]/[20px] font-[600]">First upload</span>
          <span className="text-fletchers-gray-175 text-[14px]/[20px] font-[600]">Case closed</span>
        </div>

        {isLoading ? (
          <div className="flex h-40 items-center justify-center">
            <LoadingSpinner size={40} />
          </div>
        ) : isError ? (
          <div className="text-fletchers-red-500 flex h-24 items-center justify-center text-sm">
            Failed to load expired cases.
          </div>
        ) : !rows?.length ? (
          <div className="text-fletchers-gray-200 flex h-24 items-center justify-center text-sm">
            No expired cases for this organisation.
          </div>
        ) : (
          <Virtuoso
            style={{ height: Math.min(rows.length * ROW_HEIGHT, MAX_LIST_HEIGHT) }}
            data={rows}
            fixedItemHeight={ROW_HEIGHT}
            computeItemKey={(_index, row) => row.caseId}
            itemContent={(index, row) => (
              // Separator on top of every row but the first — virtualised rows
              // are separate wrappers, so `last:` can't drop a trailing border.
              <div className={`border-fletchers-gray-110 h-10 ${index === 0 ? '' : 'border-t'} ${gridClass}`}>
                <span className="text-fletchers-black-900 truncate text-sm" title={row.caseReference}>
                  {row.caseReference}
                </span>
                <span className="text-fletchers-gray-200 text-sm">{formatOptionalDate(row.expiryDate)}</span>
                <span className="text-fletchers-gray-200 text-sm">{formatDaysUntilExpiry(row.daysUntilExpiry)}</span>
                <span className="text-fletchers-gray-200 text-sm">
                  {row.expiryReason ? (EXPIRY_REASON_LABELS[row.expiryReason] ?? row.expiryReason) : '—'}
                </span>
                <span className="text-fletchers-gray-200 text-sm">{formatOptionalDate(row.dateOfFirstUpload)}</span>
                <span className="text-fletchers-gray-200 text-sm">{formatOptionalDate(row.dateOfCaseClosure)}</span>
              </div>
            )}
          />
        )}
      </div>
    </DashboardSection>
  );
};
